import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { settingsApi } from '@/lib/api';
import { Radio, Facebook, Calendar, MapPin, Clock } from 'lucide-react';

const LiveStreamPage = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await settingsApi.get();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const getFacebookEmbed = () => {
    if (!settings?.facebook_live_url) return null;
    
    const url = settings.facebook_live_url;
    return (
      <iframe
        src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=800`}
        className="w-full aspect-video"
        style={{ border: 'none', overflow: 'hidden' }}
        scrolling="no"
        frameBorder="0"
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        title="Facebook Live Stream"
      />
    );
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-r from-red-700 to-red-800 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-4 h-4 bg-white rounded-full animate-pulse"></span>
            <Radio className="h-12 w-12 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Live Stream
          </h1>
          <p className="text-xl text-red-100 max-w-2xl mx-auto">
            Join us for worship from wherever you are
          </p>
        </div>
      </section>

      <section className="py-12 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4">
          {/* Live Stream Embed */}
          <Card className="overflow-hidden mb-8">
            <div className="bg-black">
              {settings?.facebook_live_url ? (
                getFacebookEmbed()
              ) : (
                <div className="aspect-video flex flex-col items-center justify-center bg-stone-900 text-white">
                  <Radio className="h-16 w-16 mb-4 opacity-50" />
                  <p className="text-xl font-medium">No Live Stream Currently</p>
                  <p className="text-stone-400 mt-2">Check back during service times</p>
                </div>
              )}
            </div>
          </Card>

          {/* Service Info */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="p-6 text-center">
                <Calendar className="h-8 w-8 text-amber-700 mx-auto mb-3" />
                <h3 className="font-semibold text-stone-800">When</h3>
                <p className="text-stone-600">Every Sunday</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <Clock className="h-8 w-8 text-amber-700 mx-auto mb-3" />
                <h3 className="font-semibold text-stone-800">Time</h3>
                <p className="text-stone-600">10:00 AM EST</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 text-amber-700 mx-auto mb-3" />
                <h3 className="font-semibold text-stone-800">Platform</h3>
                <p className="text-stone-600">Facebook Live</p>
              </CardContent>
            </Card>
          </div>

          {/* Facebook Link */}
          <Card>
            <CardContent className="p-6 text-center">
              <Facebook className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-stone-800 mb-2">Watch on Facebook</h2>
              <p className="text-stone-600 mb-4">
                Follow us on Facebook to watch our live streams and get notifications when we go live.
              </p>
              <a
                href="https://www.facebook.com/stjohnsucc"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Facebook className="h-5 w-5" />
                Follow on Facebook
              </a>
            </CardContent>
          </Card>

          {/* Info */}
          <div className="mt-8 text-center text-stone-600">
            <p>
              Can't watch live? All our services are recorded and available on our{' '}
              <a href="/sermons" className="text-amber-700 hover:underline">Sermons page</a>{' '}
              shortly after the service.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LiveStreamPage;
